/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Event type.
 */
export const EventType = {
  Warning: "warning",
  Breach: "breach",
} as const;
/**
 * Event type.
 */
export type EventType = ClosedEnum<typeof EventType>;

export type SpendRuleEvent = {
  /**
   * When the event was recorded.
   */
  createdAt: Date;
  /**
   * Actor display name, when known.
   */
  displayName?: string | undefined;
  /**
   * Actor email.
   */
  email: string;
  /**
   * Event type.
   */
  eventType: EventType;
  /**
   * The event ID.
   */
  id: string;
  /**
   * Per-person budget in USD the rule granted at the pinned version.
   */
  limitUsd: number;
  /**
   * ID of the exact rule version row that produced the event.
   */
  ruleId: string;
  /**
   * Name of the rule as of the version that fired.
   */
  ruleName: string;
  /**
   * Versioned rule URN pinning the config that produced the event.
   */
  ruleUrn: string;
  /**
   * Actor spend in USD at evaluation time.
   */
  spendUsd: number;
  /**
   * Speakeasy user ID of the actor, when linked.
   */
  userId?: string | undefined;
  /**
   * Exclusive end of the budget window; the budget resets at this instant.
   */
  windowEnd: Date;
  /**
   * Inclusive start of the budget window.
   */
  windowStart: Date;
};

/** @internal */
export const EventType$inboundSchema: z.ZodMiniEnum<typeof EventType> = z.enum(
  EventType,
);

/** @internal */
export const SpendRuleEvent$inboundSchema: z.ZodMiniType<
  SpendRuleEvent,
  unknown
> = z.pipe(
  z.object({
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    display_name: z.optional(z.string()),
    email: z.string(),
    event_type: EventType$inboundSchema,
    id: z.string(),
    limit_usd: z.number(),
    rule_id: z.string(),
    rule_name: z.string(),
    rule_urn: z.string(),
    spend_usd: z.number(),
    user_id: z.optional(z.string()),
    window_end: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    window_start: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "created_at": "createdAt",
      "display_name": "displayName",
      "event_type": "eventType",
      "limit_usd": "limitUsd",
      "rule_id": "ruleId",
      "rule_name": "ruleName",
      "rule_urn": "ruleUrn",
      "spend_usd": "spendUsd",
      "user_id": "userId",
      "window_end": "windowEnd",
      "window_start": "windowStart",
    });
  }),
);

export function spendRuleEventFromJSON(
  jsonString: string,
): SafeParseResult<SpendRuleEvent, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SpendRuleEvent$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SpendRuleEvent' from JSON`,
  );
}
